import { useEffect, useState } from 'react';
import { ensureAnonymousSession, appCheckReady, fn as callables, logWebEvent } from '../firebase';
import { isAndroid, isIOS } from './platform';

// Desktop/Chromium PWA install. Our manifest + service worker (WP4-A) make the
// site installable; on Chrome/Edge (incl. desktop) the browser fires a
// `beforeinstallprompt` event we can defer and trigger from our own button —
// a real one-click install, instead of sending desktop users to the iOS App
// Store (which they can't use). Captured at module scope because the event can
// fire before any component mounts.
let deferredPrompt = null;
const subs = new Set();
function notify() {
  subs.forEach((fn) => fn(!!deferredPrompt));
}

// One localStorage key gates EVERY install / Add-to-Home-Screen prompt across
// the app (the AppShell banner + the post-submit Play card + the Profile
// conversion card). Set when the user explicitly dismisses ("Not interested")
// OR when the browser confirms an install (`appinstalled`). The value is the
// dismissal timestamp and expires after 14 days (funnel audit R12): a "not
// now" in week 1 must not silence the install ask forever — the user who
// declines pre-first-reveal is a different person three weeks in. Legacy '1'
// values (the old permanent flag) are treated as expired so long-silenced
// devices get one fresh ask. `isStandalone()` remains the suppressor for
// users already running the installed PWA.
const DISMISS_KEY = 'ps_install_dismissed';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // suppress Chrome's mini-infobar; we drive the prompt
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    // They installed it — silence the pitch (TTL'd like any dismissal; the
    // standalone check + Chrome's own installed-state cover the long tail).
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* private mode — best effort */
    }
    notify();
    // Record the install as a web→web-app conversion (the precise "install
    // moment"; Android Chrome fires this reliably). recordPwaInstall is
    // idempotent + locally-guarded, so the standalone-launch trigger in
    // AppShell calling it too is harmless.
    recordPwaInstall('appinstalled');
  });
}

// One device-local flag so we hit the markPwaInstalled callable AT MOST once per
// device — not on every standalone launch. The SERVER is idempotent regardless
// (first pwaInstalledAt wins), but this avoids a needless callable on every open.
const INSTALL_RECORDED_KEY = 'ps_pwa_install_recorded';
let _recordingInstall = false;

function installPlatform() {
  if (isAndroid()) return 'android';
  if (isIOS()) return 'ios';
  return 'desktop';
}

/**
 * Record a web-app install (PWA / Add-to-Home-Screen) as a conversion — the
 * Android/desktop analog of the iOS download bonus. Stamps
 * users/{uid}.pwaInstalledAt via the markPwaInstalled callable (server-written,
 * idempotent) and fires the GA4 `web_pwa_installed` event, both at most once per
 * device. `source` is 'appinstalled' (the precise install moment, Android
 * Chrome) or 'standalone_launch' (catches devices that installed before this
 * shipped, on their next open from the home screen). Best-effort: on failure we
 * do NOT set the local flag, so a later launch retries. Anonymous-safe — web
 * players have an anon uid; we ensure a session first.
 */
export async function recordPwaInstall(source) {
  try {
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(INSTALL_RECORDED_KEY) === '1'
    ) {
      return;
    }
    if (_recordingInstall) return; // de-dupe concurrent calls within a session
    _recordingInstall = true;

    await ensureAnonymousSession(); // anon uid so the callable has request.auth
    await appCheckReady; // warm the App Check token before the first callable (cold-mint guard)
    const res = await callables.markPwaInstalled();
    const data = (res && res.data) || {}; // { marked, reason? }

    // No user doc yet (installed on a brand-new session before the profile was
    // created): DON'T burn the local flag — let the next standalone launch retry
    // once the doc exists. Nothing was recorded, so emit no event either.
    if (data.reason === 'no-user') return;

    // Emit the GA4 event ONLY on a fresh server stamp (marked === true). The
    // server marker is deduped by pwaInstalledAt, but GA4 has no such dedup, so
    // firing on a repeat / already-marked call (e.g. cleared local flag) would
    // inflate the event stream. Either way we set the local flag below to stop
    // retrying.
    if (data.marked === true) {
      logWebEvent('web_pwa_installed', { platform: installPlatform(), source });
    }
    try {
      localStorage.setItem(INSTALL_RECORDED_KEY, '1');
    } catch {
      /* private mode — best effort; we'll just retry next launch */
    }
  } catch (e) {
    console.warn('[app-web] recordPwaInstall failed (will retry next launch):', e);
  } finally {
    _recordingInstall = false;
  }
}

/** True when the app is running as an installed PWA (home screen / dock). */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.navigator.standalone === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

/**
 * True if the user dismissed the install prompt (or installed) within the last
 * 14 days. Legacy '1' (the old permanent flag) parses to NaN → treated as
 * expired, so devices silenced under the old rule get a fresh ask.
 */
export function installDismissed() {
  try {
    if (typeof localStorage === 'undefined') return false;
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false; // legacy '1' → expired
    return Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/** Dismiss every install / A2HS prompt across the app for the next 14 days. */
export function dismissInstall() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode — best effort */
  }
}

/**
 * Should install / A2HS prompts be HIDDEN? True when the app is already
 * installed (standalone) OR the user dismissed the prompt. Read at render time;
 * surfaces with a dismiss control keep their own state for an instant hide.
 */
export function hideInstallPrompts() {
  return isStandalone() || installDismissed();
}

/** React hook: is the PWA installable right now (a deferred prompt is held)? */
export function usePwaInstallable() {
  const [can, setCan] = useState(!!deferredPrompt);
  useEffect(() => {
    const fn = (v) => setCan(v);
    subs.add(fn);
    setCan(!!deferredPrompt); // re-sync in case the event fired pre-mount
    return () => subs.delete(fn);
  }, []);
  return can;
}

/** Fire the native install prompt. → 'accepted' | 'dismissed' | 'unavailable'. */
export async function promptPwaInstall() {
  if (!deferredPrompt) return 'unavailable';
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  notify();
  return outcome;
}
